/*
 * Slide-out cart drawer
 * ---------------------
 * Custom upgrade-safe replacement for the theme's native ATC flyout, gated by
 * the `enable_slide_out_cart` setting. Opens on add-to-cart (via the
 * `product:added` event from custom-upsell.js), on `cart:adding` (fired the
 * instant an add starts, so the panel feels immediate), and on cart-icon click.
 *
 * Performance notes:
 *  - Event delegation: listeners are bound once to the drawer, so they survive
 *    the innerHTML swaps in refreshDrawer() (no re-attaching every refresh).
 *  - Optimistic UI: quantity +/- and remove update the row, line total, subtotal
 *    and header count instantly, then a debounced server call reconciles exact
 *    values (discounts, free-shipping bar) in the background.
 */
document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('#CartDrawer');
  if (!drawer) return;

  const body = document.body;
  const moneyFormat = drawer.dataset.moneyFormat || '${{amount}}';

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */
  const formatMoney = (cents) => {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  };

  const getRows = () =>
    Array.prototype.slice.call(drawer.querySelectorAll('[data-cartitem]'));

  const recomputeLineTotal = (row, qty) => {
    const unit = parseInt(row.dataset.cartitemPrice, 10) || 0;
    const totalEl = row.querySelector('[data-cartitem-total]');
    if (totalEl) totalEl.innerHTML = formatMoney(unit * qty);
  };

  const recomputeSubtotal = () => {
    let subtotal = 0;
    getRows().forEach((row) => {
      const unit = parseInt(row.dataset.cartitemPrice, 10) || 0;
      const input = row.querySelector('[data-quantity-input]');
      const qty = input ? parseInt(input.value, 10) || 0 : 0;
      subtotal += unit * qty;
    });
    const subtotalEl = drawer.querySelector('.cart-drawer__subtotal .money');
    if (subtotalEl) subtotalEl.innerHTML = formatMoney(subtotal);
    return subtotal;
  };

  const setHeaderCount = (count) => {
    const headerCount = document.querySelector('[data-header-cart-count]');
    if (!headerCount) return;
    if (count > 0) {
      headerCount.setAttribute('data-header-cart-count', count);
      headerCount.classList.add('visible');
    } else {
      headerCount.setAttribute('data-header-cart-count', '');
      headerCount.classList.remove('visible');
    }
  };

  const currentCount = () =>
    getRows().reduce((sum, row) => {
      const input = row.querySelector('[data-quantity-input]');
      return sum + (input ? parseInt(input.value, 10) || 0 : 0);
    }, 0);

  const toggleMinusButtons = () => {
    drawer.querySelectorAll('[data-quantity-input]').forEach((input) => {
      const wrapper = input
        .closest('[data-quantity-selector]')
        ?.querySelector('[data-button-wrapper-minus]');
      if (!wrapper) return;
      const minusButton = wrapper.querySelector('[data-quantity-minus]');
      const disabled = (parseInt(input.value, 10) || 0) <= 1;
      wrapper.classList.toggle('quantity-selector__button-wrapper--disabled', disabled);
      if (minusButton) {
        if (disabled) minusButton.setAttribute('disabled', 'disabled');
        else minusButton.removeAttribute('disabled');
      }
    });
  };

  /* ------------------------------------------------------------------ */
  /* Open / close                                                        */
  /* ------------------------------------------------------------------ */
  const openDrawer = () => {
    drawer.classList.add('active');
    drawer.setAttribute('aria-hidden', 'false');
    body.classList.add('cart-drawer-open');
  };

  const closeDrawer = () => {
    drawer.classList.remove('active');
    drawer.setAttribute('aria-hidden', 'true');
    body.classList.remove('cart-drawer-open');
  };

  const setLoading = (on) => drawer.classList.toggle('is-loading', !!on);

  /* ------------------------------------------------------------------ */
  /* Order note (optional, gated by `enable_cart_drawer_notes`)          */
  /* ------------------------------------------------------------------ */
  // The note field lives outside the regions refreshDrawer() rewrites, so it is
  // bound once here and its value is never clobbered mid-typing. It posts with
  // the drawer's checkout form and is also saved to the cart as the customer
  // types, so the note survives checking out from /cart or an accelerated
  // checkout button.
  const noteWrapper = drawer.querySelector('[data-cart-drawer-note]');
  const noteInput = drawer.querySelector('[data-cart-drawer-note-input]');
  const noteStatus = drawer.querySelector('[data-cart-drawer-note-status]');
  let noteTimer = null;
  let noteRequestId = 0;
  let savedNote = noteInput ? noteInput.value : '';

  const setNoteStatus = (key) => {
    if (!noteStatus || !noteWrapper) return;
    const text = key ? noteWrapper.dataset[key] || '' : '';
    noteStatus.textContent = text;
    noteStatus.classList.toggle('cart-drawer__note-status--error', key === 'noteError');
  };

  const saveNote = () => {
    if (!noteInput) return Promise.resolve();
    const value = noteInput.value;
    if (value === savedNote) return Promise.resolve();

    // Guard against an older save resolving after a newer one and rewinding
    // the "last saved" value.
    const requestId = ++noteRequestId;

    setNoteStatus('noteSaving');
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: value })
    })
      .then((res) => {
        if (!res.ok) throw new Error('cart/update.js responded ' + res.status);
        return res.json();
      })
      .then(() => {
        if (requestId !== noteRequestId) return;
        savedNote = value;
        // Keep the /cart page's own note field in step, so checking out from
        // there doesn't post a stale note over the one just typed.
        const pageNote = document.querySelector('#cart-note');
        if (pageNote && pageNote !== noteInput) pageNote.value = value;
        setNoteStatus('noteSaved');
        setTimeout(() => {
          // Only clear if nothing newer has been typed since.
          if (noteInput.value === savedNote) setNoteStatus(null);
        }, 2000);
      })
      .catch((err) => {
        console.error('[cart-drawer] note save failed', err);
        if (requestId === noteRequestId) setNoteStatus('noteError');
      });
  };

  if (noteInput) {
    noteInput.addEventListener('input', () => {
      clearTimeout(noteTimer);
      noteTimer = setTimeout(saveNote, 700);
    });

    noteInput.addEventListener('blur', () => {
      clearTimeout(noteTimer);
      saveNote();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Server refresh (reconcile exact totals / shipping / item markup)    */
  /* ------------------------------------------------------------------ */
  // updateItems=false skips re-rendering the item list (used after optimistic
  // quantity/remove changes so the rows don't visibly re-render).
  const refreshDrawer = (updateItems = true) => {
    return Promise.all([
      fetch('/cart?view=drawer').then((res) => res.text()),
      fetch('/cart.js').then((res) => res.json())
    ])
      .then(([html, cart]) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        if (updateItems) {
          const newItems = doc.querySelector('[data-cart-drawer-items]');
          const itemsTarget = drawer.querySelector('[data-cart-drawer-items]');
          if (newItems && itemsTarget) itemsTarget.innerHTML = newItems.innerHTML;
        }

        const newShipping = doc.querySelector('[data-cart-drawer-shipping]');
        const shippingTarget = drawer.querySelector('[data-cart-drawer-shipping]');
        if (shippingTarget) shippingTarget.innerHTML = newShipping ? newShipping.innerHTML : '';

        const newFooter = doc.querySelector('.cart-drawer__footer');
        const footerTarget = drawer.querySelector('.cart-drawer__footer');
        if (newFooter && footerTarget) footerTarget.innerHTML = newFooter.innerHTML;
        else if (!newFooter && footerTarget) footerTarget.remove();
        else if (newFooter && !footerTarget)
          drawer.querySelector('.cart-drawer__inner').appendChild(newFooter);

        setHeaderCount(cart.item_count);
        toggleMinusButtons();

        // The note block isn't re-rendered above, so just keep it in step with
        // the cart: hide it when empty, and pick up a note set elsewhere
        // (the /cart page, another tab) unless the customer is mid-edit.
        if (noteWrapper) noteWrapper.hidden = cart.item_count === 0;
        if (
          noteInput &&
          document.activeElement !== noteInput &&
          noteInput.value === savedNote &&
          (cart.note || '') !== noteInput.value
        ) {
          noteInput.value = cart.note || '';
          savedNote = noteInput.value;
        }
      })
      .finally(() => setLoading(false));
  };

  /* ------------------------------------------------------------------ */
  /* Cart line changes (optimistic + debounced)                          */
  /* ------------------------------------------------------------------ */
  const pendingTimers = {};

  const serverChange = (key, quantity, reconcileItems) => {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then((res) => res.json())
      .then(() => refreshDrawer(reconcileItems))
      .catch((err) => {
        console.error('[cart-drawer] change failed', err);
        refreshDrawer(true); // fall back to a full reconcile on error
      });
  };

  const debouncedServerChange = (key, quantity) => {
    clearTimeout(pendingTimers[key]);
    pendingTimers[key] = setTimeout(() => serverChange(key, quantity, false), 300);
  };

  const setQuantity = (row, newQty) => {
    newQty = Math.max(0, newQty);
    const key = row.dataset.cartitemKey;
    if (newQty === 0) return removeRow(row);

    const input = row.querySelector('[data-quantity-input]');
    if (input) input.value = newQty;
    recomputeLineTotal(row, newQty);
    recomputeSubtotal();
    setHeaderCount(currentCount());
    toggleMinusButtons();
    debouncedServerChange(key, newQty);
  };

  const removeRow = (row) => {
    const key = row.dataset.cartitemKey;
    row.classList.add('cart-item--removing');
    clearTimeout(pendingTimers[key]);

    setTimeout(() => {
      row.remove();
      recomputeSubtotal();
      setHeaderCount(currentCount());
      // Empty state if that was the last line.
      if (getRows().length === 0) {
        if (noteWrapper) noteWrapper.hidden = true;
        const itemsTarget = drawer.querySelector('[data-cart-drawer-items]');
        if (itemsTarget) {
          itemsTarget.innerHTML =
            '<p class="cart-drawer__empty">' +
            (window.Theme && window.Theme.strings && window.Theme.strings.cartEmpty
              ? window.Theme.strings.cartEmpty
              : 'Your cart is empty') +
            '</p>';
        }
      }
    }, 220);

    serverChange(key, 0, getRows().length <= 1); // reconcile items if cart empties
  };

  /* ------------------------------------------------------------------ */
  /* Delegated events                                                    */
  /* ------------------------------------------------------------------ */
  drawer.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-cart-drawer-close]');
    if (closeBtn) {
      e.preventDefault();
      return closeDrawer();
    }

    const removeBtn = e.target.closest('[data-cartitem-remove]');
    if (removeBtn) {
      e.preventDefault();
      const row = removeBtn.closest('[data-cartitem]');
      if (row) removeRow(row);
      return;
    }

    const plusBtn = e.target.closest('[data-quantity-plus]');
    if (plusBtn) {
      e.preventDefault();
      const row = plusBtn.closest('[data-cartitem]');
      const input = row && row.querySelector('[data-quantity-input]');
      if (input) {
        const inc = parseInt(input.dataset.incrementValue, 10) || 1;
        setQuantity(row, (parseInt(input.value, 10) || 0) + inc);
      }
      return;
    }

    const minusBtn = e.target.closest('[data-quantity-minus]');
    if (minusBtn) {
      e.preventDefault();
      const row = minusBtn.closest('[data-cartitem]');
      const input = row && row.querySelector('[data-quantity-input]');
      if (input) {
        const inc = parseInt(input.dataset.incrementValue, 10) || 1;
        setQuantity(row, (parseInt(input.value, 10) || 0) - inc);
      }
      return;
    }
  });

  drawer.addEventListener('change', (e) => {
    const input = e.target.closest('[data-quantity-input]');
    if (!input) return;
    const row = input.closest('[data-cartitem]');
    if (row) setQuantity(row, parseInt(input.value, 10) || 0);
  });

  // The overlay lives outside #CartDrawer; close on click.
  const overlay = document.querySelector('.cart-drawer__overlay');
  if (overlay) overlay.addEventListener('click', closeDrawer);

  // Esc to close.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('active')) closeDrawer();
  });

  /* ------------------------------------------------------------------ */
  /* Open triggers                                                       */
  /* ------------------------------------------------------------------ */
  const bagIcon = document.querySelector('[data-cart-icon]');
  if (bagIcon) {
    bagIcon.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer();
      setLoading(true);
      refreshDrawer();
    });
  }

  // Fired the instant an add-to-cart request starts -> open immediately.
  document.addEventListener('cart:adding', () => {
    openDrawer();
    setLoading(true);
  });

  // Fired once the item (and any upsells) are in the cart -> show fresh content.
  document.addEventListener('product:added', () => {
    openDrawer();
    refreshDrawer();
  });

  toggleMinusButtons();
});
